import moment from 'moment';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { MatTimeService } from '@nimbus-library/features/shared/components/material/mat-time/mat-time.service';

export enum MatTimeValuePart {
  DAY = 'day',
  HOUR = 'hour',
  MINUTE = 'minute',
  SECOND = 'second',
}

export class MatTimeValueProcessor {
  private daysMax: number = 31;
  private hoursMax: number = 23;
  private minutesMax: number = 59;
  private secondsMax: number = 59;

  public value: IMap<string> = {
    [MatTimeValuePart.DAY]: '',
    [MatTimeValuePart.HOUR]: '',
    [MatTimeValuePart.MINUTE]: '',
    [MatTimeValuePart.SECOND]: '',
  };

  public defaultValue: IMap<string> = {
    [MatTimeValuePart.DAY]: this.matTimeService.formatNumber(0),
    [MatTimeValuePart.HOUR]: this.matTimeService.formatNumber(0),
    [MatTimeValuePart.MINUTE]: this.matTimeService.formatNumber(0),
    [MatTimeValuePart.SECOND]: this.matTimeService.formatNumber(0),
  };

  public isDaysValid: boolean = true;
  public isHoursValid: boolean = true;
  public isMinutesValid: boolean = true;
  public isSecondsValid: boolean = true;

  public constructor(private matTimeService: MatTimeService) {}

  public parseValueSeconds(seconds: number = null): void {
    if (seconds) {
      const value = moment.duration(seconds, 'seconds');
      this.value[MatTimeValuePart.DAY] = this.matTimeService.formatNumber(value.asDays());
      this.value[MatTimeValuePart.HOUR] = this.matTimeService.formatNumber(value.hours());
      this.value[MatTimeValuePart.MINUTE] = this.matTimeService.formatNumber(value.minutes());
      this.value[MatTimeValuePart.SECOND] = this.matTimeService.formatNumber(value.seconds());
    } else {
      this.value[MatTimeValuePart.DAY] = this.defaultValue[MatTimeValuePart.DAY];
      this.value[MatTimeValuePart.HOUR] = this.defaultValue[MatTimeValuePart.HOUR];
      this.value[MatTimeValuePart.MINUTE] = this.defaultValue[MatTimeValuePart.MINUTE];
      this.value[MatTimeValuePart.SECOND] = this.defaultValue[MatTimeValuePart.SECOND];
    }
  }

  public parseValue(value: Date = null): void {
    if (value) {
      this.value[MatTimeValuePart.DAY] = this.matTimeService.formatNumber(value.getDate() - 1);
      this.value[MatTimeValuePart.HOUR] = this.matTimeService.formatNumber(value.getHours());
      this.value[MatTimeValuePart.MINUTE] = this.matTimeService.formatNumber(value.getMinutes());
      this.value[MatTimeValuePart.SECOND] = this.matTimeService.formatNumber(value.getSeconds());
    } else {
      this.value[MatTimeValuePart.DAY] = this.defaultValue[MatTimeValuePart.DAY];
      this.value[MatTimeValuePart.HOUR] = this.defaultValue[MatTimeValuePart.HOUR];
      this.value[MatTimeValuePart.MINUTE] = this.defaultValue[MatTimeValuePart.MINUTE];
      this.value[MatTimeValuePart.SECOND] = this.defaultValue[MatTimeValuePart.SECOND];
    }
  }

  public getDurationSeconds(): number {
    const duration = moment.duration({
      days: parseInt(this.value[MatTimeValuePart.DAY], 0) || 0,
      hour: parseInt(this.value[MatTimeValuePart.HOUR], 0) || 0,
      minutes: parseInt(this.value[MatTimeValuePart.MINUTE], 0) || 0,
      seconds: parseInt(this.value[MatTimeValuePart.SECOND], 0) || 0,
    });
    return duration.asSeconds();
  }

  public setSelectedTimeValue(toDateValue: Date = null, isDaysEnabled: boolean = false): Date {
    const result: Date = toDateValue ? new Date(toDateValue) : new Date();
    const resultDateDay = result.getDate();
    const valueDateDay = parseInt(this.value[MatTimeValuePart.DAY], 0) + 1;
    const day = isDaysEnabled ? valueDateDay : resultDateDay;
    result.setDate(day || 0);
    result.setHours(parseInt(this.value[MatTimeValuePart.HOUR], 0) || 0);
    result.setMinutes(parseInt(this.value[MatTimeValuePart.MINUTE], 0) || 0);
    result.setSeconds(parseInt(this.value[MatTimeValuePart.SECOND], 0) || 0);
    return result;
  }

  public onIncreaseDecreaseValue(valuePart: MatTimeValuePart, step: number, event: WheelEvent): void {
    const currentValue: number = parseInt(this.value[valuePart], 0) || 0;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    let processedValue: number = currentValue + step * (event.deltaY / -100);
    if (valuePart === MatTimeValuePart.DAY) {
      processedValue = this.calculateValueWithThreshold(processedValue, 0, this.daysMax);
    } else if (valuePart === MatTimeValuePart.HOUR) {
      processedValue = this.calculateValueWithThreshold(processedValue, 0, this.hoursMax);
    } else if (valuePart === MatTimeValuePart.MINUTE || valuePart === MatTimeValuePart.SECOND) {
      processedValue = this.calculateValueWithThreshold(processedValue, 0, this.minutesMax);
    }
    this.value[valuePart] = this.matTimeService.formatNumber(processedValue);
  }

  public normalizeValue(valuePart: MatTimeValuePart): void {
    const processedValue: number = parseInt(this.value[valuePart], 0) || 0;
    this.value[valuePart] = this.matTimeService.formatNumber(processedValue);
  }

  public set days(value: string) {
    this.value[MatTimeValuePart.DAY] = value;
    this.isHoursValid = true;
  }

  public get days(): string {
    return this.value[MatTimeValuePart.DAY];
  }

  public set hours(value: string) {
    const numberValue: number = parseInt(value, 0) || 0;
    this.value[MatTimeValuePart.HOUR] = value;
    this.isHoursValid = numberValue <= this.hoursMax;
  }

  public get hours(): string {
    return this.value[MatTimeValuePart.HOUR];
  }

  public set minutes(value: string) {
    const numberValue: number = parseInt(value, 0) || 0;
    this.value[MatTimeValuePart.MINUTE] = value;
    this.isMinutesValid = numberValue <= this.minutesMax;
  }

  public get minutes(): string {
    return this.value[MatTimeValuePart.MINUTE];
  }

  public set seconds(value: string) {
    const numberValue: number = parseInt(value, 0) || 0;
    this.value[MatTimeValuePart.SECOND] = value;
    this.isSecondsValid = numberValue <= this.secondsMax;
  }

  public get seconds(): string {
    return this.value[MatTimeValuePart.SECOND];
  }

  public get isValid(): boolean {
    return this.isDaysValid && this.isHoursValid && this.isMinutesValid && this.isSecondsValid;
  }

  private calculateValueWithThreshold(value: number, min: number, max: number): number {
    max = max + 1;
    if (value >= max) {
      value = value - max;
    } else if (value < min) {
      value = max + value;
    }
    return value;
  }
}
